import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const CODE_LENGTH = 6;

type Props = {
  /** Вызывается, когда набраны все цифры — и при вставке, и при ручном вводе. */
  onComplete: (code: string) => void;
  /** Текущий код наружу: кнопка «Подтвердить» знает, полон ли он. */
  onChange?: (code: string) => void;
  disabled?: boolean;
  /** Сброс после неверного кода: смена значения очищает клетки. */
  resetKey?: number;
};

/**
 * Шесть клеток под код из письма. Вынесено из экрана подтверждения почты, когда
 * появился второй такой экран (вход по коду): вся возня с вставкой, Backspace и
 * стрелками должна жить в одном месте, иначе один экран чинят, а второй нет.
 */
export function CodeInput({ onComplete, onChange, disabled, resetKey = 0 }: Props) {
  const { t } = useTranslation();
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const seenReset = useRef(resetKey);

  if (seenReset.current !== resetKey) {
    seenReset.current = resetKey;
    setDigits(Array(CODE_LENGTH).fill(''));
    inputs.current[0]?.focus();
  }

  const apply = (next: string[]) => {
    setDigits(next);
    const code = next.join('');
    onChange?.(code);
    if (code.length === CODE_LENGTH && !next.includes('')) onComplete(code);
  };

  const onChangeDigit = (index: number, raw: string) => {
    const value = raw.replace(/\D/g, '');
    if (!value) {
      const next = [...digits];
      next[index] = '';
      apply(next);
      return;
    }
    // Клавиатура телефона и вставка могут прислать сразу несколько цифр —
    // раскладываем их по клеткам, а не теряем всё кроме первой.
    const next = [...digits];
    let cursor = index;
    for (const ch of value) {
      if (cursor >= CODE_LENGTH) break;
      next[cursor] = ch;
      cursor += 1;
    }
    inputs.current[Math.min(cursor, CODE_LENGTH - 1)]?.focus();
    apply(next);
  };

  const onKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !digits[index] && index > 0) {
      // Пустая клетка + Backspace — стираем предыдущую цифру: иначе курсор
      // застревает и код приходится чистить мышью.
      e.preventDefault();
      const next = [...digits];
      next[index - 1] = '';
      apply(next);
      inputs.current[index - 1]?.focus();
    }
    if (e.key === 'ArrowLeft' && index > 0) inputs.current[index - 1]?.focus();
    if (e.key === 'ArrowRight' && index < CODE_LENGTH - 1) inputs.current[index + 1]?.focus();
  };

  const onPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
    if (!pasted) return;
    e.preventDefault();
    const next = Array(CODE_LENGTH).fill('');
    pasted.split('').forEach((ch, i) => (next[i] = ch));
    inputs.current[Math.min(pasted.length, CODE_LENGTH - 1)]?.focus();
    apply(next);
  };

  return (
    <div className="flex justify-between gap-2" onPaste={onPaste}>
      {digits.map((digit, i) => (
        <input
          key={i}
          ref={(el) => {
            inputs.current[i] = el;
          }}
          value={digit}
          onChange={(e) => onChangeDigit(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          inputMode="numeric"
          autoComplete={i === 0 ? 'one-time-code' : 'off'}
          aria-label={t('verifyEmail.digitLabel', { n: i + 1 })}
          autoFocus={i === 0}
          disabled={disabled}
          className="h-14 w-full rounded-button bg-surface-container-lowest text-center font-display text-2xl font-bold text-on-surface shadow-soft focus:outline-none focus:shadow-ambient"
        />
      ))}
    </div>
  );
}
